import { customTheme } from '@upright/ui/common/generic/theme/custom-theme';
import { css } from 'styled-components';
import { TextSize } from '.';
import { CssFromFunction } from '../../../types/types';
import { fluid, fluidSpaceLg } from '../../../utilities/fluid/fluid';
import { BreakPointsAndValues } from '../../../utilities/fluid/types';
import { getFontSizes } from './text-scale';

/* RN 2.10.2023:
   Stuff below is for platform text, using size constants aligned with Primer theme but keeping the old getTextStyle parameters (bolding, margin).
 */

// Types
interface TextStyleOptions {
  size?: TextSize;
  isBold?: boolean;
  withMargin?: boolean;
}

interface PlatformTextStyle {
  fontSize?: string;
  style: CssFromFunction;
}

// Constants
export const bouTextFont = `'Helvetica Neue', Roboto, sans-serif`;
export const bouTextFontStyle = `font-family: ${bouTextFont}`;
export const textBaseStyle = css`
  ${bouTextFontStyle};
  line-height: 128.8%;
`;
const baseMargin = fluidSpaceLg('margin-bottom', 20);

/**
 * @deprecated since 2.10.2023
 * Use primarily TextPlatform component for new body text.
 * If it doesn't cover your use case, refer to the font sizes in customTheme (src/ui/views/root/custom-theme.ts).
 */
export function getTextStyle({ size, isBold, withMargin }: TextStyleOptions = {}): string {
  const textSize = (() => {
    switch (size) {
      case 'xxxxs':
      case 'xxxs':
      case 'xxs':
        /* RN: We reference directly to the customTheme since the themeGet function did not work reliably in testing.
        Discouraged in documentation (https://primer.style/react/theming#referencing-theme-values) but works. */
        return customTheme.fontSizes[0]; // rem equivalent to 12px
      case 'xsmall':
      case 'small':
      case 'medium':
        return customTheme.fontSizes[1]; // rem equivalent to 14px
      case 'large':
        return customTheme.fontSizes[2]; // rem equivalent to 16px
      case 'huge':
        return customTheme.fontSizes[3]; // rem equivalent to 20px
      case undefined:
        return undefined;
      default:
        throw Error('getTextStyle should not arrive here');
    }
  })();
  const { fontSize, style }: PlatformTextStyle = { fontSize: textSize, style: textBaseStyle };
  return `
      ${fontSize ? `font-size: ${fontSize};` : ''}
      ${withMargin ? baseMargin : ''}
      ${isBold ? 'font-weight: bold;' : ''}
      ${style.join('')}
      `;
}

/* RN 2.10.2023:
   Stuff below is for facade text, using the old fluid scale & fluid functions.
*/
interface TextStyle {
  fontSizes?: BreakPointsAndValues;
  style: CssFromFunction;
}

function scaleTextStyle(size: number) {
  const ret: TextStyle = {
    fontSizes: getFontSizes(size),
    style: textBaseStyle,
  };
  return ret;
}

const textXXXXS = scaleTextStyle(-2.5);
const textXXXS = scaleTextStyle(-2.0);
const textXXS = scaleTextStyle(-1.5);
const textXS = scaleTextStyle(-1.0);
const textSmall = scaleTextStyle(-0.5);
const textMedium = scaleTextStyle(0);
const textLarge = scaleTextStyle(0.5);
const textHuge = scaleTextStyle(5);

export function getFacadeTextStyle({ size, isBold, withMargin }: TextStyleOptions = {}): string {
  const { fontSizes, style }: TextStyle = (() => {
    switch (size) {
      case 'xxxxs':
        return textXXXXS;
      case 'xxxs':
        return textXXXS;
      case 'xxs':
        return textXXS;
      case 'xsmall':
        return textXS;
      case 'small':
        return textSmall;
      case 'medium':
        return textMedium;
      case 'large':
        return textLarge;
      case 'huge':
        return textHuge;
      case undefined:
        return { fontSizes: undefined, style: textBaseStyle };
      default:
        throw Error();
    }
  })();
  return `
      ${fontSizes ? fluid('font-size', fontSizes) : ''}
      ${withMargin ? baseMargin : ''}
      ${isBold ? 'font-weight: bold;' : ''}
      ${style.join('')}
      `;
}
